import {
  CSP_DASHBOARD_LOADING,
  CSP_DASHBOARD_LOADED,
  CSP_DASHBOARD_FAILED,
  CSP_SYNC_TRIGGERING,
  CSP_SYNC_TRIGGERED,
  CSP_SYNC_TRIGGER_FAILED,
  CSP_TENANTS_LOADED,
  CSP_UNMAPPED_TENANTS_LOADED,
  CSP_TENANT_UPDATING,
  CSP_TENANT_UPDATED,
  CSP_TENANT_UPDATE_FAILED,
  CSP_NOTIFICATIONS_LOADED,
  CSP_SETTINGS_LOADING,
  CSP_SETTINGS_LOADED,
  CSP_SETTINGS_SAVING,
  CSP_SETTINGS_SAVED,
  CSP_SETTINGS_FAILED,
  CSP_PRODUCTS_LOADING,
  CSP_PRODUCTS_LOADED,
  CSP_PRODUCTS_FAILED,
  CSP_PRODUCT_UPDATING,
  CSP_PRODUCT_UPDATED,
  CSP_PRODUCT_UPDATE_FAILED,
} from './cspRenewalsTypes'
import api from '../../services/api'
import { addErrorToast, addSaveToast } from './companyActionCreators'

// ─── Dashboard ───────────────────────────────────────────────────────

export const fetchDashboard = (includeExcluded = false) => async (dispatch) => {
  try {
    dispatch({ type: CSP_DASHBOARD_LOADING })
    const url = includeExcluded ? '/csp/dashboard?includeExcluded=true' : '/csp/dashboard'
    const res = await api.get(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_DASHBOARD_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_DASHBOARD_FAILED })
    dispatch(addErrorToast('Failed to load CSP renewals dashboard'))
  }
}

// ─── Sync ────────────────────────────────────────────────────────────

export const triggerSync = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_SYNC_TRIGGERING })
    const res = await api.post('/csp/sync')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_SYNC_TRIGGERED, payload: data })
    dispatch(addSaveToast('CSP sync queued - check Hangfire dashboard for progress'))
  } catch (err) {
    dispatch({ type: CSP_SYNC_TRIGGER_FAILED })
    dispatch(addErrorToast('Failed to trigger CSP sync'))
  }
}

// ─── Tenants ─────────────────────────────────────────────────────────

export const fetchTenants = (includeInactive = false) => async (dispatch) => {
  try {
    const url = includeInactive ? '/csp/tenants?includeInactive=true' : '/csp/tenants'
    const res = await api.get(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_TENANTS_LOADED, payload: data })
  } catch (err) {
    dispatch(addErrorToast('Failed to load CSP tenants'))
  }
}

// All subscriptions for a DD tenant, including inactive/removed/suspended.
// Drives the Tenant Settings drawer's "Subscriptions" tab — the table
// filters / sorts / paginates client-side from this single response.
// Returns the array (or [] on error) instead of dispatching into the store
// because the data is drawer-local and stale-on-reopen is the right policy.
export const fetchTenantSubscriptions = (ddTenantId) => async (dispatch) => {
  try {
    const res = await api.get(`/csp/tenants/${encodeURIComponent(ddTenantId)}/subscriptions`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } catch (err) {
    dispatch(addErrorToast('Failed to load subscriptions for this tenant'))
    return []
  }
}

// Trigger the active-tenants CSV download. Reads the filename from the
// server's Content-Disposition header so the date stamp comes from the
// server's "today" (matches the row data). Falls back to a generic name
// if the header is missing. Returns true on success so the caller can
// re-enable the button.
export const exportActiveTenantsCsv = () => async (dispatch) => {
  try {
    const res = await api.get('/csp/tenants/export')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const blob = await res.blob()

    let filename = 'csp-active-tenants.csv'
    const disposition = res.headers.get('Content-Disposition')
    if (disposition) {
      // RFC 5987: prefer filename*=UTF-8''<encoded> when present (correctly
      // round-trips non-ASCII), fall back to the legacy filename="..." form.
      // ASP.NET Core's File(..., name) currently emits only the legacy form,
      // so the fallback is what we hit today; the RFC5987 branch is here for
      // future-proofing if the response shape ever changes.
      const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
      if (utf8Match && utf8Match[1]) {
        filename = decodeURIComponent(utf8Match[1].trim())
      } else {
        const legacyMatch = /filename=(?:"([^"]+)"|([^;]+))/i.exec(disposition)
        if (legacyMatch) filename = (legacyMatch[1] || legacyMatch[2]).trim()
      }
    }

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    return true
  } catch (err) {
    dispatch(addErrorToast('Failed to export CSP tenants'))
    return false
  }
}

// Unified type-ahead search across Managed + Essentials + raw ConnectWise,
// deduped by CW int with UserManager matches first. The picker doesn't need
// to know which source each row came from - clicking any row dispatches
// mapTenantToCw and the server's reuse-first policy resolves to the right
// backing Guid.
export const searchCompanies = (query, limit = 20) => async (dispatch) => {
  if (!query || !query.trim()) return []
  try {
    const params = new URLSearchParams({ q: query, limit: String(limit) })
    const res = await api.get(`/csp/companies/search?${params.toString()}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } catch (err) {
    dispatch(addErrorToast('Company search failed'))
    return []
  }
}

// Tenant mapping. includeInactive controls whether the post-map refetch
// includes inactive tenants (the Tenants page always fetches the full set).
// Reuse-first server-side: the chosen CW int may resolve to an existing
// Managed/Essentials Guid, an existing CSPCWCompany shadow Guid, or a freshly
// created shadow - the picker doesn't need to know which.
export const mapTenantToCw = (tenantId, cwCompanyId, includeInactive = false) => async (dispatch) => {
  try {
    dispatch({ type: CSP_TENANT_UPDATING, payload: tenantId })
    const res = await api.post(`/csp/tenants/${tenantId}/map-cw`, { cwCompanyId })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_TENANT_UPDATED, payload: tenantId })
    dispatch(addSaveToast('Tenant mapped to ConnectWise company'))
    dispatch(fetchTenants(includeInactive))
    dispatch(fetchUnmappedTenants(includeInactive))
    dispatch(fetchDashboard())
  } catch (err) {
    dispatch({ type: CSP_TENANT_UPDATE_FAILED, payload: tenantId })
    dispatch(addErrorToast(err.message || 'Failed to map tenant'))
  }
}

export const fetchUnmappedTenants = (includeInactive = false) => async (dispatch) => {
  try {
    const url = includeInactive ? '/csp/tenants/unmapped?includeInactive=true' : '/csp/tenants/unmapped'
    const res = await api.get(url)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_UNMAPPED_TENANTS_LOADED, payload: data })
  } catch (err) {
    dispatch(addErrorToast('Failed to load unmapped CSP tenants'))
  }
}

export const unmapTenant = (tenantId, includeInactive = false) => async (dispatch) => {
  try {
    dispatch({ type: CSP_TENANT_UPDATING, payload: tenantId })
    const res = await api.deleteCall(`/csp/tenants/${tenantId}/map`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    dispatch({ type: CSP_TENANT_UPDATED, payload: tenantId })
    dispatch(addSaveToast('Tenant unmapped'))
    dispatch(fetchTenants(includeInactive))
    dispatch(fetchUnmappedTenants(includeInactive))
    dispatch(fetchDashboard())
  } catch (err) {
    dispatch({ type: CSP_TENANT_UPDATE_FAILED, payload: tenantId })
    dispatch(addErrorToast('Failed to unmap tenant'))
  }
}

export const setContactOverride = (tenantId, email, includeInactive = false) => async (dispatch) => {
  try {
    dispatch({ type: CSP_TENANT_UPDATING, payload: tenantId })
    const res = await api.put(`/csp/tenants/${tenantId}/contact`, { email })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_TENANT_UPDATED, payload: tenantId })
    dispatch(addSaveToast(email ? 'Contact override saved' : 'Contact override cleared'))
    dispatch(fetchTenants(includeInactive))
    // Override changes flip the "no recipients" count, so refresh the
    // dashboard summary tile too.
    dispatch(fetchDashboard())
  } catch (err) {
    dispatch({ type: CSP_TENANT_UPDATE_FAILED, payload: tenantId })
    dispatch(addErrorToast(err.message || 'Failed to update contact override'))
  }
}

export const setContactExcluded = (tenantId, cwContactId, isExcluded, includeInactive = false) => async (dispatch) => {
  try {
    dispatch({ type: CSP_TENANT_UPDATING, payload: tenantId })
    const res = await api.put(`/csp/tenants/${tenantId}/contacts/${cwContactId}`, { isExcluded })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_TENANT_UPDATED, payload: tenantId })
    dispatch(fetchTenants(includeInactive))
    dispatch(fetchDashboard())
  } catch (err) {
    dispatch({ type: CSP_TENANT_UPDATE_FAILED, payload: tenantId })
    dispatch(addErrorToast(err.message || 'Failed to toggle recipient'))
  }
}

// ─── Notifications ───────────────────────────────────────────────────

export const fetchNotifications = () => async (dispatch) => {
  try {
    const res = await api.get('/csp/notifications')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_NOTIFICATIONS_LOADED, payload: data })
  } catch (err) {
    dispatch(addErrorToast('Failed to load notification history'))
  }
}

// ─── Settings ────────────────────────────────────────────────────────

export const fetchSettings = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_SETTINGS_LOADING })
    const res = await api.get('/csp/settings')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_SETTINGS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_SETTINGS_FAILED })
    dispatch(addErrorToast('Failed to load CSP renewal settings'))
  }
}

export const previewTemplate = (
  { track, tenantId, companyId, renewalMonth, renewalMonths, renewalDates, template },
) => async (dispatch) => {
  try {
    const res = await api.post('/csp/settings/preview', {
      track,
      tenantId,
      companyId,
      renewalMonth,
      // Multi-month coalesced preview - queue's preview button passes a list
      // matching what the dispatcher will actually send. Settings modal
      // callers omit this and rely on the singular renewalMonth.
      renewalMonths,
      // Exact-batch preview - queue passes the specific RenewalDate values
      // its row covers so the preview filters to only those subs (not every
      // sub in the same month, which would over-include subs that belong to
      // later dispatcher runs).
      renewalDates,
      template,
    })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    return await res.json()
  } catch (err) {
    dispatch(addErrorToast(err.message || 'Failed to render preview'))
    return null
  }
}

// ─── Notification queue ──────────────────────────────────────────────

export const fetchQueue = (bucket = 'all', { unmappedOnly = false } = {}) => async (dispatch) => {
  try {
    dispatch({ type: 'CSP_QUEUE_LOADING' })
    const params = new URLSearchParams({ bucket })
    if (unmappedOnly) params.set('unmappedOnly', 'true')
    const res = await api.get(`/csp/queue?${params.toString()}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: 'CSP_QUEUE_LOADED', payload: data })
  } catch (err) {
    dispatch({ type: 'CSP_QUEUE_FAILED' })
    dispatch(addErrorToast('Failed to load CSP notification queue'))
  }
}

export const setTenantRenewalsDisabled = (tenantId, disabled, includeInactive = false) => async (dispatch) => {
  try {
    dispatch({ type: CSP_TENANT_UPDATING, payload: tenantId })
    const res = await api.put(`/csp/tenants/${tenantId}/disabled`, { disabled })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_TENANT_UPDATED, payload: tenantId })
    dispatch(addSaveToast(disabled ? 'Renewals disabled for tenant' : 'Renewals re-enabled for tenant'))
    dispatch(fetchTenants(includeInactive))
    dispatch(fetchUnmappedTenants(includeInactive))
  } catch (err) {
    dispatch({ type: CSP_TENANT_UPDATE_FAILED, payload: tenantId })
    dispatch(addErrorToast(err.message || 'Failed to update tenant'))
  }
}

export const saveSettings = (settings) => async (dispatch) => {
  try {
    dispatch({ type: CSP_SETTINGS_SAVING })
    const res = await api.put('/csp/settings', settings)
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_SETTINGS_SAVED, payload: settings })
    dispatch(addSaveToast('CSP renewal settings saved'))
  } catch (err) {
    dispatch({ type: CSP_SETTINGS_FAILED })
    dispatch(addErrorToast(err.message || 'Failed to save settings'))
  }
}

// ─── Products (curation) ─────────────────────────────────────────────

export const fetchProducts = () => async (dispatch) => {
  try {
    dispatch({ type: CSP_PRODUCTS_LOADING })
    const res = await api.get('/csp/products')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: CSP_PRODUCTS_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: CSP_PRODUCTS_FAILED })
    dispatch(addErrorToast('Failed to load CSP products'))
  }
}

export const setProductExcluded = (stockCode, isExcluded) => async (dispatch) => {
  try {
    dispatch({ type: CSP_PRODUCT_UPDATING, payload: stockCode })
    const res = await api.put(`/csp/products/${encodeURIComponent(stockCode)}`, { isExcluded })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: CSP_PRODUCT_UPDATED, payload: { stockCode, isExcluded } })
    dispatch(fetchDashboard())
  } catch (err) {
    dispatch({ type: CSP_PRODUCT_UPDATE_FAILED, payload: stockCode })
    dispatch(addErrorToast(err.message || 'Failed to toggle product'))
  }
}
